import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useFundingPageDimensions = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [collapsedMode, setCollapsedMode] = useState(false);
  const [collapsedPanels, setCollapsedPanels] = useState({
    details: false,
    profile: false,
  });

  const panelGroupRef = useRef(null);

  const detailsRef = useRef(null);
  const profileRef = useRef(null);

  const collapsedSize = (1440 * 2) / windowWidth;
  const minSizes = useMemo(
    () => [
      windowWidth >= 1200 ? (1200 * 30) / windowWidth : 30,
      windowWidth >= 1200 ? (1200 * 70) / windowWidth : 70,
    ],
    [windowWidth],
  );
  const defaultSizes = useMemo(() => [30, 70], []);

  const expandPanel = useCallback(
    idx => {
      if (collapsedMode) {
        if (profileRef.current.isCollapsed()) {
          detailsRef.current.collapse();
          profileRef.current.expand();
        } else {
          profileRef.current.collapse();
          detailsRef.current.expand();
        }

        setCollapsedPanels(prev => ({ ...prev, details: idx !== 0, profile: idx !== 1 }));
      } else {
        panelGroupRef.current.setLayout(defaultSizes);

        setCollapsedPanels(prev => ({ ...prev, details: false, profile: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collapsedMode],
  );

  useEffect(() => {
    if (windowWidth < 1200) {
      if (panelGroupRef.current) {
        if (!detailsRef.current.isCollapsed() && !profileRef.current.isCollapsed()) {
          profileRef.current.collapse();
        }

        setCollapsedMode(true);
      }
    } else if (collapsedMode) {
      setCollapsedMode(false);
      panelGroupRef.current.setLayout(defaultSizes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth]);

  const toggleProfilePanel = useCallback(() => {
    if (collapsedPanels.details) {
      setCollapsedPanels(prev => ({ ...prev, details: false }));
      panelGroupRef.current.setLayout(defaultSizes);
    } else {
      setCollapsedPanels(prev => ({ ...prev, details: true }));
      detailsRef.current?.collapse();
    }
  }, [collapsedPanels, defaultSizes]);

  return {
    collapsedMode,
    collapsedPanels,
    setCollapsedPanels,
    expandPanel,
    panelGroupRef,
    detailsRef,
    profileRef,
    toggleProfilePanel,
    collapsedSize,
    minSizes,
    defaultSizes,
  };
};
